import {
  CompanyContractFromApi,
  CreateCompanyContractResponse,
  GetCompanyContractResponse
} from '@interfaces/CompanyContract.interface';
import BaseApiClient from '../BaseApiClient';

const API_VERSION = {
  Version: '2025-11-14'
};

export type CreateCompanyContractPayload = {
  name: string;
  document: File | null;
  contract_type_id?: number | null;
  legal_entity_id?: number | null;
  counterparty_role_id?: number | null;
  company_id?: number | null;
  task_id?: number | null;
};

export type UpdateCompanyContractPayload = {
  name: string;
  start_date: string | null;
  end_date: string | null;
  payment_terms: string | null;
  risk_transfer: string | null;
  comingling: string | null;
  termination_noticed_period: string | null;
  counterparty_name: string | null;
  is_executed_toba: boolean;
  is_draft?: boolean;
  contract_type_id: number;
  legal_entity_id: number;
  counterparty_role_id: number;
  company_id: number;
  address: {
    line_1: string;
    line_2: string | null;
    town: string;
    postcode: string;
  } | null;
  teams: Array<{
    contract_role_id: number;
    team_id: number;
    user_id: number;
  }>;
  task_id?: number | null;
};

export class ApiCompanyContract extends BaseApiClient {
  /**
   * Create a contract (POST /companies/contracts) with multipart/form-data
   */
  async create(
    payload: CreateCompanyContractPayload
  ): Promise<CreateCompanyContractResponse> {
    const formData = new FormData();
    formData.append('name', payload.name);
    if (payload.document) formData.append('document', payload.document);
    if (payload.contract_type_id != null && payload.contract_type_id !== 0) {
      formData.append('contract_type_id', String(payload.contract_type_id));
    }
    if (payload.legal_entity_id != null && payload.legal_entity_id !== 0) {
      formData.append('legal_entity_id', String(payload.legal_entity_id));
    }
    if (
      payload.counterparty_role_id != null &&
      payload.counterparty_role_id !== 0
    ) {
      formData.append(
        'counterparty_role_id',
        String(payload.counterparty_role_id)
      );
    }
    if (payload.company_id != null && payload.company_id !== 0) {
      formData.append('company_id', String(payload.company_id));
    }
    if (payload.task_id != null && payload.task_id !== 0) {
      formData.append('task_id', String(payload.task_id));
    }

    return new Promise((resolve, reject) => {
      this.client
        .post('companies/contracts', formData, {
          headers: {
            ...API_VERSION,
            'Content-Type': 'multipart/form-data'
          }
        })
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  /**
   * Get a contract by id (GET /contracts/{id}).
   * Returns the flat API shape; use mappers to get contract_details/review for forms.
   */
  async get(id: number): Promise<GetCompanyContractResponse> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`contracts/${id}`, {
          headers: API_VERSION
        })
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  async list(companyId: number): Promise<CompanyContractFromApi[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/companies/${companyId}/contracts`)
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  /**
   * Update a contract (PUT /contracts/{contract})
   */
  async update(
    contractId: number,
    payload: UpdateCompanyContractPayload
  ): Promise<CreateCompanyContractResponse> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`contracts/${contractId}`, payload, {
          headers: { ...API_VERSION, 'Content-Type': 'application/json' }
        })
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  async deleteCompanyContract(contractId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`contracts/${contractId}`, {
          headers: API_VERSION
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }
}

export { companyContractQueryKeys } from './query-keys';
export {
  useCompanyContractListQuery,
  useCompanyContractQuery
} from './use-company-contract-queries';
export {
  useCompanyContractCreateMutation,
  useCompanyContractDeleteMutation,
  useCompanyContractUpdateMutation
} from './use-company-contract-mutations';
